import axios from "axios";
import { AXIOS_CONFIG } from "@/utils/api";
import { backend_url } from "@/constants";
import type { OnboardingStepsResponse } from "../components/types";

/**
 * Fetches the onboarding steps status from the backend.
 * Returns list of onboarding steps with their completion status.
 *
 * @returns {Promise<OnboardingStepsResponse>} A promise that resolves to the onboarding steps response
 * @throws {Error} If the API request fails
 */
export const fetchOnboardingSteps =
  async (): Promise<OnboardingStepsResponse> => {
    try {
      const response = await axios.get(`${backend_url}/api/onboarding/steps/`, {
        ...AXIOS_CONFIG,
      });

      return response.data.content;
    } catch (error) {
      console.error("Failed to load onboarding steps", error);
      throw new Error("Failed to load onboarding steps");
    }
  };

/**
 * Marks the Wizzy Do onboarding step as visited.
 * Called when user opens the "What Can Wizzy Do?" Notion page.
 *
 * @returns {Promise<void>}
 */
export const markWizzyVisited = async (): Promise<void> => {
  try {
    await axios.post(
      `${backend_url}/api/onboarding/steps/wizzy-visited/`,
      {},
      {
        ...AXIOS_CONFIG,
      }
    );
  } catch (error) {
    console.error("Failed to mark Wizzy visited", error);
    // Don't throw - this is a non-critical tracking call
  }
};

/**
 * Marks an intake documentation page as visited.
 * Called when user opens TaxDome, Taxes To Go, or Google Drive integration documentation.
 *
 * @param {string} docUrl - The documentation URL identifier (e.g., 'taxslayer-taxdome')
 * @returns {Promise<void>}
 */
export const markIntakeDocsVisited = async (docUrl: string): Promise<void> => {
  try {
    await axios.post(
      `${backend_url}/api/onboarding/steps/intake-docs-visited/`,
      { doc_url: docUrl },
      {
        ...AXIOS_CONFIG,
      }
    );
  } catch (error) {
    console.error("Failed to mark intake docs visited", error);
    // Don't throw - this is a non-critical tracking call
  }
};

/**
 * Marks the Referral onboarding step as visited.
 * Called when user clicks on the referral card and views the referral page.
 *
 * @returns {Promise<void>}
 */
export const markReferralVisited = async (): Promise<void> => {
  try {
    await axios.post(
      `${backend_url}/api/onboarding/steps/referral-visited/`,
      {},
      {
        ...AXIOS_CONFIG,
      }
    );
  } catch (error) {
    console.error("Failed to mark Referral visited", error);
    // Don't throw - this is a non-critical tracking call
  }
};

/**
 * Marks the Customize OneClick onboarding step as visited.
 * Called when user clicks on the customize one-click card and views the settings page.
 *
 * @returns {Promise<void>}
 */
export const markCustomizeOneClickVisited = async (): Promise<void> => {
  try {
    await axios.post(
      `${backend_url}/api/onboarding/steps/customize-oneclick-visited/`,
      {},
      {
        ...AXIOS_CONFIG,
      }
    );
  } catch (error) {
    console.error("Failed to mark Customize OneClick visited", error);
    // Don't throw - this is a non-critical tracking call
  }
};
